import React, { memo } from "react";
import {
  Card,
  CardHeader,
  IconButton,
  Grid,
  Typography,
  Paper,
  ClickAwayListener,
  MenuList,
  MenuItem,
  ListItemIcon,
  Popper,
  Grow,
} from "@material-ui/core";
import ChevronRightRoundedIcon from "@material-ui/icons/ChevronRightRounded";
import CheckIcon from "@material-ui/icons/Check";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import { makeStyles } from "@material-ui/core/styles";
import { TaskIcon, MenuDot, CompleteIcon } from "./Icons";
import { Link, useRouteMatch, useHistory } from "react-router-dom";
import { MentorshipTask } from "../server/serverTypes";
import { actions as taskActions } from "../task/redux/model";
import { useDispatch } from "react-redux";
import { stripTime, stripDate } from "../user/utils";

const useStyles = makeStyles((theme) => ({
  root: {
    // maxWidth: 370,
    height: "100px",
    background: "#FCFAF5",
    borderRadius: "5px",
    margin: "10px 0px",
    filter: "drop-shadow(0px 4px 15px rgba(0, 0, 0, 0.25))",
    [theme.breakpoints.down("md")]: {
      width: "100%",
      maxWidth: "100%",
    },
  },
  cardAction: {
    margin: 0,
  },
  header: {
    fontFamily: "Inter",
    fontStyle: "normal",
    fontWeight: "bold",
    fontSize: "12px",
    lineHeight: "140%",
    color: "#00664F",
  },
  main: {
    fontFamily: "Inter",
    fontStyle: "normal",
    fontWeight: "normal",
    fontSize: "16px",
    color: "#272727",
  },
  tinyText: {
    fontFamily: "Inter",
    fontStyle: "normal",
    fontWeight: "normal",
    fontSize: "12px",
    color: "#757575",
  },
  menuBtn: {
    padding: 0,
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  subContent: {
    fontFamily: "Inter",
    fontStyle: "normal",
    fontWeight: "normal",
    fontSize: "14px",
    color: "rgba(0, 0, 0, 0.87)",
  },
}));

interface IPendingTaskComponent {
  withArrow?: boolean;
  task: MentorshipTask;
  isMentee?: boolean;
  handleDelete: (id: number) => void;
  handleUpdate: (id: number) => void;
  hideOption?: boolean;
}

const PendingTaskComponent = ({
  withArrow = true,
  task,
  isMentee = false,
  handleDelete,
  handleUpdate,
  hideOption = false,
}: IPendingTaskComponent) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  let { path, url } = useRouteMatch();

  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClickAway = () => {
    setOpen(false);
  };

  const handleClose = (event: any, action: any) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);

    if (action === "delete") {
      handleDelete(task.id);
    }
    if (action === "complete") {
      handleUpdate(task.id);
    }
  };

  function handleListKeyDown(event: any) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    }
  }

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false && anchorRef.current) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  // const handleDelete = () => {
  //   dispatch(taskActions.deleteMentorshipTask({ id: task.id }));
  // };

  // const handleUpdate = () => {
  //   dispatch(taskActions.completeTask({ id: task.id }));
  // };

  return (
    <Card className={classes.root}>
      <CardHeader
        classes={{ action: classes.cardAction }}
        style={{ padding: "8px 16px", height: "100%" }}
        avatar={<TaskIcon />}
        action={
          <React.Fragment>
            <div style={{ textAlign: "right" }}>
              {hideOption ? null : isMentee === true ? (
                <CompleteIcon />
              ) : (
                <>
                  <IconButton
                    ref={anchorRef}
                    aria-controls={open ? "menu-list-grow" : undefined}
                    aria-haspopup="true"
                    onClick={handleToggle}
                    classes={{ root: classes.menuBtn }}
                  >
                    <MenuDot />
                  </IconButton>
                  <Popper open={open} anchorEl={anchorRef.current} transition>
                    {({ TransitionProps, placement }) => (
                      <Grow
                        {...TransitionProps}
                        style={{
                          transformOrigin:
                            placement === "bottom"
                              ? "center top"
                              : "center bottom",
                        }}
                      >
                        <Paper>
                          <ClickAwayListener onClickAway={handleClickAway}>
                            <MenuList
                              autoFocusItem={open}
                              id="menu-list-grow"
                              onKeyDown={handleListKeyDown}
                            >
                              <Link
                                to={`${url}/edit-task/${task.id}`}
                                style={{ textDecoration: "none" }}
                              >
                                <MenuItem
                                  onClick={(e) => {
                                    handleClose(e, "edit");
                                  }}
                                >
                                  <ListItemIcon style={{ minWidth: "30px" }}>
                                    <EditIcon fontSize="small" />
                                  </ListItemIcon>
                                  <Typography className={classes.subContent}>
                                    Edit
                                  </Typography>
                                </MenuItem>
                              </Link>
                              <MenuItem
                                onClick={(e) => {
                                  handleClose(e, "complete");
                                }}
                              >
                                <ListItemIcon style={{ minWidth: "30px" }}>
                                  <CheckIcon fontSize="small" />
                                </ListItemIcon>
                                <Typography className={classes.subContent}>
                                  Complete
                                </Typography>
                              </MenuItem>
                              <MenuItem
                                onClick={(e) => {
                                  handleClose(e, "delete");
                                }}
                              >
                                <ListItemIcon style={{ minWidth: "30px" }}>
                                  <DeleteIcon fontSize="small" />
                                </ListItemIcon>
                                <Typography className={classes.subContent}>
                                  Delete
                                </Typography>
                              </MenuItem>
                            </MenuList>
                          </ClickAwayListener>
                        </Paper>
                      </Grow>
                    )}
                  </Popper>
                </>
              )}
            </div>
            {withArrow ? (
              <Link to={`${url}/assignment/${task.id}`}>
                <IconButton aria-label="settings" style={{ padding: 0 }}>
                  <ChevronRightRoundedIcon fontSize="large" />
                </IconButton>
              </Link>
            ) : null}
          </React.Fragment>
        }
        title={
          <React.Fragment>
            <Grid container alignItems="center" spacing={1}>
              <Grid item>
                <Typography className={classes.header}>To Do Task</Typography>
              </Grid>
            </Grid>
          </React.Fragment>
        }
        subheader={
          <React.Fragment>
            <Typography className={classes.main}>{task.title}</Typography>
            <Typography className={classes.tinyText}>
              Due date: {stripDate(task.dueDate)}
            </Typography>
          </React.Fragment>
        }
      />
    </Card>
  );
};

export default memo(PendingTaskComponent);
